export const dsaNotes = [
  {
    id: 2001,
    topic: "dsa",
    seriesKey: "dsa",
    title: "Introduction to DSA",
    description:
      "Overview of Data Structures and Algorithms, their importance, and the mathematical framework for measuring efficiency.",
    content: `
##  What is DSA?
DSA stands for **Data Structures & Algorithms**. It is the study of **efficient ways to store, organize, and manipulate data** and the strategies to solve computational problems. DSA is crucial for writing performant, scalable code and is a core component of technical interviews.
### Time Complexity and Space Complexity
1. **Time Complexity:** Measures the amount of time an algorithm takes to run as a function of the input size ($N$).
2. **Space Complexity:** Measures the amount of memory (space) an algorithm uses as a function of the input size ($N$).
### Asymptotic Notations (The Big Three)
These notations describe the behavior of an algorithm as the input size approaches infinity.
1.  **Big O Notation ($O$):** Describes the **worst-case** (upper bound) running time.
2.  **Big Omega Notation ($Omega$):** Describes the **best-case** (lower bound) running time.
3.  **Big Theta Notation ($Theta$):** Describes the **average-case** (tight bound) running time, where Big O and Big Omega are the same.
### Recursion Basics
**Recursion** is an algorithmic technique where a function calls itself, breaking a problem down into smaller, identical subproblems until a **base case** is reached.
`,
    tags: [
      "DSA",
      "Introduction",
      "Time Complexity",
      "Big O",
      "Asymptotic Analysis",
      "Recursion",
    ],
    date: "2025-10-01",
  },
  {
    id: 2002,
    topic: "dsa",
    seriesKey: "dsa",
    title: "Arrays: Concepts and Techniques",
    description:
      "Understanding arrays, multi-dimensional structures, and advanced techniques like prefix sums and the sliding window method.",
    content: `
##  Arrays
An **array** is a collection of items stored at contiguous memory locations. It's the simplest data structure, allowing O(1) access time using an index.
### Types and Operations
1. **Types:** 1D, 2D (Matrix), and Multi-dimensional.
2. **Operations:**
     - Traversal: Visiting every element (O(N)).
     - Insertion/Deletion: Can be O(N) in the middle of a fixed-size array, as elements must be shifted.
      - Searching: O(N) for Linear Search, O(log N) for Binary Search (if sorted).
### Advanced Array Techniques
1. **Prefix Sum / Cumulative Sum:** An array where each element stores the sum of all elements before it (including itself). Used to calculate the sum of any subarray range in O(1) time.
2. **Rotation of Arrays:** Shifting elements to the left or right, often solved efficiently using array reversal techniques.
3. **Subarray Problems:** Finding the largest sum subarray (Kadane's algorithm) or counting subarrays that meet a condition.
4. **Sliding Window Technique:** Used primarily on arrays/strings to find maximum/minimum values in a continuous subarray of a specified size or condition, often optimizing an O(N^2) brute-force solution to O(N).
`,
    tags: ["DSA", "Arrays", "Prefix Sum", "Sliding Window", "Rotation"],
    date: "2025-10-02",
  },
  {
    id: 2003,
    topic: "dsa",
    seriesKey: "dsa",
    title: "Strings and Pattern Searching",
    description:
      "Working with string representation, common operations, and specialized pattern matching algorithms.",
    content: `
##  Strings
In C and many languages, a **string** is an array of characters.
### Basic Operations
1. **Concatenation, Substring, Comparison:** These form the basis of string manipulation.
2. **Palindrome Problems:** Checking if a string reads the same forwards and backwards.
### Pattern Searching Algorithms
These algorithms find all occurrences of a smaller pattern string (P) within a larger text string (T).
1.  **Naive Search:** Compares P with every M-length window in T. Time Complexity is O(NM).
2.  **KMP Algorithm (Knuth-Morris-Pratt):** Uses a preprocessing step (LPS array) to avoid unnecessary backward shifts of the pattern. Time Complexity is O(N+M).
3.  **Rabin-Karp Algorithm:** Uses hashing to quickly check if a pattern matches a text window, resorting to character-by-character comparison only on hash collision. Average Time Complexity is O(N+M).
`,
    tags: [
      "DSA",
      "Strings",
      "KMP",
      "Rabin-Karp",
      "Pattern Matching",
      "Palindrome",
    ],
    date: "2025-10-03",
  },
  {
    id: 2004,
    topic: "dsa",
    seriesKey: "dsa",
    title: "Linked Lists: Types and Operations",
    description:
      "The concept of linked nodes, various list types, key operations, and applications like cycle detection.",
    content: `
##  Linked Lists
A **Linked List** is a linear data structure where elements (nodes) are not stored at contiguous memory locations. Each node stores data and a pointer (or link) to the next node.
### Types of Linked Lists
1.  **Singly Linked List:** Nodes link only to the next node.
2.  **Doubly Linked List:** Nodes have links to both the previous and the next node, allowing traversal in both directions.
3.  **Circular Linked List:** The last node's pointer points back to the first node.
### Operations and Applications
1. **Operations:** Traversal (O(N)), Insertion (O(1) at start/end if pointer available), Deletion, Searching (O(N)).
2. **Reverse Linked List:** A classic problem where all node pointers are reversed, usually done iteratively or recursively.
3. **Detecting Loops (Floyd’s Cycle Detection / Tortoise and Hare):** Uses two pointers (slow and fast) to detect if a cycle exists in O(N) time and O(1) space.
4. **Applications:** Used to implement Stacks and Queues, and dynamic memory allocation.
`,
    tags: [
      "DSA",
      "Linked Lists",
      "Singly",
      "Doubly",
      "Circular",
      "Floyd’s Cycle Detection",
    ],
    date: "2025-10-04",
  },
  {
    id: 2005,
    topic: "dsa",
    seriesKey: "dsa",
    title: "Stacks: LIFO Principle and Applications",
    description:
      "Understanding the Last-In, First-Out (LIFO) structure, implementation details, and key uses in computing.",
    content: `
##  Stacks
A **Stack** is an abstract data type (ADT) that follows the **LIFO (Last-In, First-Out)** principle. Think of a stack of plates—the last plate added is the first one removed.
### Implementation and Operations
1. **Implementation:** Can be built using an Array (simple but fixed size) or a Linked List (dynamic size).
2. **Operations (All O(1)):**
    * push: Adds an element to the top of the stack.
    * pop: Removes and returns the element from the top.
    * peek: Returns the top element without removing it.
### Applications of Stacks
1. **Expression Evaluation:** Converting and evaluating mathematical expressions (Infix to Postfix/Prefix, and evaluation).
2. **Balanced Parentheses:** Checking if all parentheses, brackets, and braces in an expression are properly nested and balanced.
3. **Next Greater Element:** Finding the next element greater than the current element in a sequence, often solved efficiently using a Monotonic Stack.
`,
    tags: [
      "DSA",
      "Stacks",
      "LIFO",
      "push",
      "pop",
      "Expression Evaluation",
      "Parentheses",
    ],
    date: "2025-10-05",
  },
  {
    id: 2006,
    topic: "dsa",
    seriesKey: "dsa",
    title: "Queues: FIFO Principle and Variations",
    description:
      "Understanding the First-In, First-Out (FIFO) structure and its major variations like Circular, Priority, and Deques.",
    content: `
##  Queues
A **Queue** is an abstract data type (ADT) that follows the **FIFO (First-In, First-Out)** principle. Think of a line of people waiting—the person who enters first is the person served first.
### Types of Queues
1.  **Simple Queue:** Standard FIFO structure.
2.  **Circular Queue:** Uses a fixed-size array where the last element points back to the first, utilizing empty space efficiently.
3.  **Priority Queue:** Elements are served based on an assigned priority, not insertion order. Typically implemented using a **Heap**.
4.  **Deque (Double-Ended Queue):** Allows insertion and deletion from both the front and the rear.
### Implementation and Applications
1. **Implementation:** Usually via Array or Linked List. A queue can also be implemented using two stacks.
2. **Applications:** Scheduling tasks (CPU scheduling, print spooling), Breadth-First Search (BFS) algorithm.
`,
    tags: [
      "DSA",
      "Queues",
      "FIFO",
      "Circular Queue",
      "Priority Queue",
      "Deque",
      "BFS",
    ],
    date: "2025-10-06",
  },
  {
    id: 2007,
    topic: "dsa",
    seriesKey: "dsa",
    title: "Recursion and Backtracking",
    description:
      "Detailed look at recursive types and the powerful Backtracking technique for solving constraint satisfaction problems.",
    content: `
##  Recursion & Backtracking
### Types of Recursion
1.  **Tail Recursion:** The recursive call is the very last operation in the function. Compilers can often optimize this to iteration, avoiding stack overflow.
2.  **Head Recursion:** The recursive call is the first statement in the function.
3.  **Tree Recursion:** The function makes multiple recursive calls (e.g., Fibonacci function).
### Backtracking
**Backtracking** is an algorithmic technique for solving problems recursively by trying to build a solution incrementally, one piece at a time. If a partial solution is found to be invalid, the algorithm "backtracks" to the previous decision point and tries a different path.
### Backtracking Problems
1. **N-Queens Problem:** Placing N non-attacking queens on an N \times N chessboard.
2. **Rat in a Maze:** Finding a path from a start point to an end point in a maze.
3. **Sudoku Solver:** Filling a 9 \times 9 grid with digits so that each column, row, and 3 \times 3 subgrid contains all digits from 1 to 9.
4. **Subset/Permutation Generation:** Finding all possible combinations or orderings of a given set of elements.
`,
    tags: [
      "DSA",
      "Recursion",
      "Backtracking",
      "N-Queens",
      "Tail Recursion",
      "Permutation",
    ],
    date: "2025-10-07",
  },
  {
    id: 2008,
    topic: "dsa",
    seriesKey: "dsa",
    title: "Hashing and Collision Handling",
    description:
      "Introduction to hash functions, techniques for resolving collisions, and real-world hash table applications.",
    content: `
##  Hashing
**Hashing** is the technique of mapping a large amount of data (keys) to a smaller, fixed-size value (hash value or index). A **Hash Table** uses a hash function to compute an index into an array of buckets or slots, from which the desired value can be found. Ideally, searching, insertion, and deletion are O(1).
### Collision Handling Techniques
A **collision** occurs when two different keys hash to the same index.
1.  **Chaining:** Each slot in the hash table array points to a Linked List containing all keys that hash to that index.
2.  **Open Addressing:** If a collision occurs, the algorithm looks for the next available empty slot using a probe sequence.
    * Linear Probing: Looks at i+1, i+2, i+3, ... slots.
    * Quadratic Probing: Looks at i+1^2, i+2^2, i+3^2, ... slots.
    * Double Hashing: Uses a second hash function to determine the size of the step in the probe sequence.
### Applications of Hash Tables
1. **Counting Frequencies:** Efficiently counting the occurrences of elements.
2. **Two-Sum Problem:** Finding pairs in an array that sum to a target value in O(N) time.
3. **Subarray Sum Problems:** Tracking cumulative sums to solve problems like finding a subarray with a specific target sum.
`,
    tags: [
      "DSA",
      "Hashing",
      "Hash Table",
      "Collision Handling",
      "Chaining",
      "Open Addressing",
      "Two-Sum",
    ],
    date: "2025-10-08",
  },
  {
    id: 2009,
    topic: "dsa",
    seriesKey: "dsa",
    title: "Trees",
    description:
      "Fundamental concepts of tree structures, different traversal methods, properties of Binary Search Trees (BSTs), and specialized structures like Heaps and Tries.",
    content: `
##  Trees
A **Tree** is a non-linear, hierarchical data structure consisting of nodes connected by edges.
### Terminology and Binary Trees
1. **Terminology:** Root, Leaf, Height (longest path from root), Depth (distance from root), Degree (number of children).
2. **Binary Trees:** Each node has at most two children (left and right).
### Traversals (O(N))
1.  **Inorder:** Left ➡️ Root ➡️ Right (yields sorted output for a BST).
2.  **Preorder:** Root ➡️ Left ➡️ Right (useful for duplicating a tree).
3.  **Postorder:** Left ➡️ Right ➡️ Root (useful for deleting a tree).
### Binary Search Trees (BST)
A BST is a binary tree where the key in the left child must be less than the root's key, and the key in the right child must be greater than the root's key. Operations (Insertion, Deletion, Searching) are O(h), where h is the height, ideally O(log N).
### Balanced Trees and Heaps
1. **Balanced Trees (AVL/Red-Black):** These are self-balancing BSTs that guarantee a height of O(log N) by performing rotations after insertion/deletion.
2. **Heaps:** A complete binary tree that satisfies the heap property:
    * Max Heap: Parent node key is ge children keys.
    * Min Heap: Parent node key is le children keys.
3. **Trie (Prefix Tree):** A specialized tree used for efficient retrieval of keys in a dataset, useful for auto-complete and spell checkers.
`,
    tags: ["DSA", "Trees", "Binary Tree", "BST", "Heap", "Traversal", "Trie"],
    date: "2025-10-09",
  },
  {
    id: 2010,
    topic: "dsa",
    seriesKey: "dsa",
    title: "Graphs",
    description:
      "The concept of graphs, methods to represent them (adjacency matrix/list), core traversal techniques (BFS/DFS), and essential algorithms like Dijkstra's and Prim's.",
    content: `
## Graphs
A **Graph** is a non-linear data structure consisting of a set of **Vertices** (nodes) and a set of **Edges** (connections) connecting them. Graphs can be Directed or Undirected.
### Representation
1.  **Adjacency Matrix:** A V \times V matrix where M[i][j]=1 if an edge exists between vertex i and j, and 0 otherwise. Space complexity is O(V^2).
2.  **Adjacency List:** An array of lists where the i-th array element stores a list of vertices adjacent to vertex i. Space complexity is O(V+E).
### Traversals
1. **BFS (Breadth-First Search):** Visits vertices layer-by-layer using a **Queue**. Finds the shortest path in an unweighted graph.
2. **DFS (Depth-First Search):** Explores as far as possible along each branch before backtracking, using a **Stack** (or recursion). Useful for topological sorting and cycle detection.
### Core Graph Algorithms
1. **Shortest Path:**
    * Dijkstra's: Finds the shortest path from a single source to all other nodes in a graph with non-negative edge weights.
    * Bellman-Ford: Finds the shortest path in graphs with negative edge weights (can detect negative cycles).
    * Floyd-Warshall:Finds the shortest paths between all pairs of vertices.
2. **Minimum Spanning Tree (MST):** A subset of edges that connects all vertices together, without any cycles and with the minimum possible total edge weight.
    * Prim's Algorithm: Builds the MST one vertex at a time.
    * Kruskal's Algorithm: Builds the MST one edge at a time, using a Disjoint Set data structure.
`,
    tags: [
      "DSA",
      "Graphs",
      "BFS",
      "DFS",
      "Dijkstra",
      "MST",
      "Adjacency List",
      "Topological Sorting",
    ],
    date: "2025-10-10",
  },
  {
    id: 2011,
    topic: "dsa",
    seriesKey: "dsa",
    title: "Searching Algorithms",
    description:
      "Comparison of linear and binary search efficiency and techniques for searching specialized arrays.",
    content: `
##  Searching Algorithms
### Linear Search
1. **Concept:** Checks every element in the list sequentially until the target is found.
2. **Time Complexity:** O(N).
### Binary Search
3. **Concept:** Requires a **sorted** array. It repeatedly divides the search interval in half.
4. **Time Complexity:** O(log N).
5. **Implementation:** Can be implemented Iteratively (using loops) or Recursively.
### Specialized Searching
1. **Search in Rotated Sorted Array:** Applying a modified binary search to find a target in a sorted array that has been rotated at some unknown pivot point.
2. **Ternary Search:** Similar to binary search but divides the array into three parts. Used to find the minimum or maximum of a unimodal function (a function that decreases then increases, or vice-versa).
`,
    tags: [
      "DSA",
      "Searching",
      "Binary Search",
      "Linear Search",
      "Ternary Search",
      "Rotated Array",
    ],
    date: "2025-10-11",
  },
  {
    id: 2012,
    topic: "dsa",
    seriesKey: "dsa",
    title: "Sorting Algorithms",
    description:
      "A comprehensive list of sorting algorithms, categorized by complexity and method (comparison vs. non-comparison).",
    content: `
##  Sorting Algorithms
Sorting algorithms arrange elements in a list in a certain order (ascending or descending).
### O(N^2) Comparison Sorts
These are simple but inefficient for large datasets.
1. **Bubble Sort:** Repeatedly steps through the list, compares adjacent elements, and swaps them if they are in the wrong order.
2. **Selection Sort:** Finds the minimum element from the unsorted portion and puts it at the beginning.
3. **Insertion Sort:** Builds the final sorted array one item at a time, inserting each new item into its correct position among the previously sorted items.
### O(N log N) Comparison Sorts
These are optimal general-purpose comparison sorts.
1. **Merge Sort:** A **Divide and Conquer** algorithm that recursively divides the array into halves, sorts them, and then merges the sorted halves.
2. **Quick Sort:** A **Divide and Conquer** algorithm that picks an element as a pivot and partitions the array around the pivot. Generally the fastest in practice.
3. **Heap Sort:** Uses a **Max Heap** to repeatedly extract the maximum element.
### Linear Time Non-Comparison Sorts
These require specific data properties (e.g., small, limited range).
1. **Counting Sort:** Sorts elements by counting the number of occurrences of each distinct element. O(N+K).
2. **Radix Sort:** Sorts integers by processing individual digits.
3. **Bucket Sort:** Divides the elements into a number of buckets, sorts each bucket, and then concatenates the results.
`,
    tags: [
      "DSA",
      "Sorting",
      "Merge Sort",
      "Quick Sort",
      "Heap Sort",
      "Bubble Sort",
      "Comparison",
    ],
    date: "2025-10-12",
  },
  {
    id: 2013,
    topic: "dsa",
    seriesKey: "dsa",
    title: "Greedy Algorithms",
    description:
      "The greedy strategy and how it's applied to optimization problems by making locally optimal choices.",
    content: `
##  Greedy Algorithms
A **Greedy Algorithm** is an approach that makes the locally optimal choice at each step with the hope of finding a global optimum. The challenge is proving that the locally optimal choice always leads to the globally optimal solution.
### Core Strategy
* Make the choice that seems best at the moment.
* The choice should be safe (it won't violate any constraints).
* The choice should leave a subproblem that is solved optimally.
### Greedy Problems
1. **Activity Selection Problem:** Given a set of activities with start and finish times, select the maximum number of non-overlapping activities. (Greedy choice: pick the activity that finishes earliest).
2. **Fractional Knapsack Problem:** An optimization problem where one can take fractional parts of items to maximize the total value in a knapsack.
*3. **Huffman Coding:** A data compression algorithm that uses a greedy approach to build an optimal prefix code tree based on character frequencies.
4. **Minimum Spanning Tree (Prim's & Kruskal's):** Both MST algorithms rely on the greedy principle of selecting the cheapest edge that does not form a cycle.
`,
    tags: [
      "DSA",
      "Greedy",
      "Optimization",
      "Knapsack",
      "Huffman Coding",
      "MST",
      "Activity Selection",
    ],
    date: "2025-10-13",
  },
  {
    id: 2014,
    topic: "dsa",
    seriesKey: "dsa",
    title: "Dynamic Programming: Memoization vs. Tabulation",
    description:
      "The core principles of Dynamic Programming (DP) for solving complex problems by breaking them into overlapping subproblems, and classic implementation patterns.",
    content: `
##  Dynamic Programming (DP)
**Dynamic Programming** is an algorithmic technique for solving optimization problems by breaking them down into simpler, overlapping subproblems and storing the results of intermediate solutions to avoid recomputing them.
### Two Key Properties for DP
1.  **Optimal Substructure:** An optimal solution to the problem contains optimal solutions to its subproblems.
2.  **Overlapping Subproblems:** The same subproblems are solved repeatedly.
### Implementation Patterns
1. **Memoization (Top-Down):** Recursive approach where the results of subproblems are stored in a lookup table (e.g., array or hash map) as they are computed.
2. **Tabulation (Bottom-Up):** Iterative approach where the results are computed for smaller subproblems first and built up to the final solution in a table.
### Classic DP Problems
1. **Fibonacci Sequence:** F(N) = F(N-1) + F(N-2).
2. **Knapsack Problem (0/1 & Unbounded):** Maximizing value given a weight constraint.
3. **Longest Common Subsequence (LCS):** Finding the longest subsequence common to two sequences.
4. **Longest Increasing Subsequence (LIS):** Finding the longest subsequence where the elements are in sorted order.
5. **Matrix Chain Multiplication:** Finding the most efficient way to multiply a sequence of matrices.
6. **Coin Change Problem:** Finding the minimum number of coins to make a certain amount.
`,
    tags: [
      "DSA",
      "DP",
      "Dynamic Programming",
      "Memoization",
      "Tabulation",
      "LCS",
      "Knapsack",
    ],
    date: "2025-10-14",
  },
  {
    id: 2015,
    topic: "dsa",
    seriesKey: "dsa",
    title: "Divide and Conquer",
    description:
      "The divide-and-conquer strategy, characterized by breaking a problem into independent subproblems, solving them, and combining the results.",
    content: `
##  Divide and Conquer
**Divide and Conquer (D&C)** is a paradigm that solves a problem by:
1.  **Divide:** Breaking the problem into several smaller subproblems of the same or related type.
2.  **Conquer:** Solving the subproblems recursively (often the base case is small enough to solve directly).
3.  **Combine:** Combining the solutions to the subproblems to get the solution to the original problem.
### D&C Problems
Unlike DP, D&C subproblems are usually **independent** (do not overlap).
1. **Merge Sort:** Divides the array in half until single elements, then merges them back in sorted order.
2. **Quick Sort:** Divides the array into two partitions around a pivot element.
3. **Binary Search:** Divides the search space in half at each step.
4. **Maximum Subarray Sum (Kadane’s Algorithm):** Although Kadane's iterative O(N) solution is preferred, the maximum subarray sum can also be solved using a Divide and Conquer approach in O(N log N).
`,
    tags: [
      "DSA",
      "Divide and Conquer",
      "Merge Sort",
      "Quick Sort",
      "Binary Search",
      "Kadane's",
    ],
    date: "2025-10-15",
  },
  {
    id: 2016,
    topic: "dsa",
    seriesKey: "dsa",
    title: "Bit Manipulation",
    description:
      "Operating directly on the binary representation of numbers using bitwise operators for speed and memory efficiency.",
    content: `
##  Bit Manipulation
**Bit Manipulation** involves performing operations directly on the individual bits of a number. This is highly efficient and necessary in system programming and certain competitive programming problems.

### Bitwise Operators
| Operator | Name | Function |
| :--- | :--- | :--- |
| \`&\` | AND | Sets bit if both corresponding bits are 1. | 
| \`^\`| XOR | Sets bit if only one of the corresponding bits is 1. |
| \`~\` | NOT | Inverts all the bits. |
| \`<<\` | Left Shift | Shifts bits to the left (multiplies by 2^k). |
| \`>>\` | Right Shift | Shifts bits to the right (divides by 2^k). |
### Common Problems and Techniques
1. **Counting Set Bits (Population Count):** Determining the number of '1' bits in a number.
2. **Checking Power of 2:** A number $N$ is a power of 2 if and only if N > 0 and (N text{ & } (N-1)) == 0.
3. **XOR Problems:** XOR is useful because A oplus A = 0 and A oplus 0 = A. Used to find the single unique number in an array where all others appear twice.
4. **Subset Generation:** Using bitmasks (binary numbers) to represent all 2^N possible subsets of a set of N elements.
`,
    tags: [
      "DSA",
      "Bit Manipulation",
      "Bitwise Operators",
      "XOR",
      "Subset Generation",
      "Optimization",
    ],
    date: "2025-10-16",
  },
  {
    id: 2017,
    topic: "dsa",
    seriesKey: "dsa",
    title: "Advanced DSA Topics",
    description:
      "An overview of specialized and high-performance data structures and algorithms, typically used for complex problem-solving.",
    content: `
##  Advanced Topics
### Disjoint Set Union (DSU) / Union-Find
1. **Concept:** A data structure that keeps track of a set of elements partitioned into a number of disjoint (non-overlapping) subsets.
2. **Operations:** **Union** (merges two sets) and **Find** (determines which set an element belongs to). Highly optimized using Path Compression and Union by Rank/Size, achieving near-constant time complexity, O(alpha(N)).
3. **Application:** Used heavily in Kruskal's MST algorithm and for detecting cycles in graphs.
### Specialized Trees
4. **Segment Tree / Fenwick Tree (Binary Indexed Tree):** Data structures used for efficient (typically O(log N)) range queries (like range sum, range minimum) and point updates on an array.
5. **Trie / Suffix Tree / Suffix Array:** Used for advanced string problems:
    * Trie: Efficient prefix-based searching.
    * Suffix Tree/Array: Used for complex string searches and pattern analysis across the entire string (suffixes).
### Advanced Graph & DP
1. **Graph Algorithms:** Deep dives into algorithms like **Floyd-Warshall** (all-pairs shortest path), **Bellman-Ford**, and advanced applications of **Dijkstra's**.
2. **Advanced DP Problems:** DP solved over complex structures like Trees or Grids (e.g., unique paths in a grid), or complex string manipulation problems.
`,
    tags: [
      "DSA",
      "Advanced",
      "Union-Find",
      "DSU",
      "Segment Tree",
      "Fenwick Tree",
      "Trie",
      "Graph Algorithms",
    ],
    date: "2025-10-17",
  },
];
